import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Plus,
  Edit3,
  Trash2,
  Eye,
  Download,
  Send,
  Bell,
  Shield,
  FileCheck,
  Users
} from 'lucide-react';
import { 
  IntakeForm, 
  IntakeField, 
  IntakeSubmission, 
  IntakeFile,
  OnboardingProject 
} from './types';

interface IntakeFormPanelProps {
  projectId?: string;
  userRole: 'client' | 'vendor' | 'admin';
  onFormSubmit?: (submission: IntakeSubmission) => void;
}

export const IntakeFormPanel: React.FC<IntakeFormPanelProps> = ({
  projectId,
  userRole,
  onFormSubmit
}) => {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<IntakeForm | null>(null);
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const [isEditingForm, setIsEditingForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState<OnboardingProject[]>([]);
  const [viewMode, setViewMode] = useState<'overview' | 'builder' | 'submissions'>('overview');

  // Mock data - replace with API calls
  useEffect(() => {
    const mockForms: IntakeForm[] = [
      {
        id: 'form-001',
        projectId: 'proj-001',
        name: 'Client Requirements Intake',
        description: 'Collect detailed requirements and documentation from TechCorp Solutions',
        fields: [
          {
            id: 'field-001',
            name: 'Company Overview',
            type: 'textarea',
            required: true,
            helpText: 'Provide a brief overview of your company and current systems'
          },
          {
            id: 'field-002',
            name: 'Current Pain Points',
            type: 'textarea',
            required: true,
            helpText: 'Describe the main challenges you\'re looking to solve'
          },
          {
            id: 'field-003',
            name: 'Integration Requirements',
            type: 'select',
            required: true,
            options: ['None', 'Basic API', 'Full Integration', 'Custom Development'],
            helpText: 'Select the level of integration needed'
          },
          {
            id: 'field-004',
            name: 'Timeline',
            type: 'date',
            required: true,
            helpText: 'When do you need this implemented?'
          },
          {
            id: 'field-005',
            name: 'Budget Range',
            type: 'select',
            required: true,
            options: ['Under $50k', '$50k-$100k', '$100k-$250k', '$250k+'],
            helpText: 'Select your budget range'
          },
          {
            id: 'field-006',
            name: 'Technical Documentation',
            type: 'file',
            required: true,
            helpText: 'Upload any existing technical documentation or specifications'
          }
        ],
        dueDate: '2024-01-30',
        assignee: 'Sarah Chen',
        status: 'active',
        submissions: [
          {
            id: 'sub-001',
            formId: 'form-001',
            submittedBy: 'John Smith',
            submittedAt: '2024-01-20T14:30:00Z',
            data: {
              'Company Overview': 'TechCorp Solutions is a mid-sized technology company...',
              'Current Pain Points': 'We need better customer data management...',
              'Integration Requirements': 'Full Integration',
              'Timeline': '2024-04-30',
              'Budget Range': '$100k-$250k'
            },
            files: [
              {
                id: 'file-001',
                name: 'techcorp-overview.pdf',
                url: '/uploads/techcorp-overview.pdf',
                size: 2048576,
                type: 'application/pdf',
                uploadedAt: '2024-01-20T14:30:00Z',
                malwareScanStatus: 'clean',
                schemaValidationStatus: 'valid'
              }
            ],
            status: 'pending',
            reviewer: 'Sarah Chen'
          }
        ],
        reminders: [
          {
            id: 'reminder-001',
            formId: 'form-001',
            type: 'email',
            frequency: 'weekly',
            message: 'Reminder: Please complete the Client Requirements Intake form',
            lastSent: '2024-01-20T09:00:00Z',
            nextSend: '2024-01-27T09:00:00Z',
            enabled: true
          }
        ]
      }
    ];

    const mockProjects: OnboardingProject[] = [
      {
        id: 'proj-001',
        name: 'Enterprise CRM Implementation',
        type: 'implementation',
        status: 'active',
        progress: 65,
        startDate: '2024-01-15',
        targetDate: '2024-04-30',
        clientName: 'TechCorp Solutions',
        projectManager: 'Sarah Chen',
        teamMembers: ['Mike Johnson', 'Lisa Wang', 'David Rodriguez', 'Emma Thompson'],
        priority: 'high',
        healthScore: 85,
        lastActivity: '2024-01-20T14:30:00Z'
      }
    ];

    setForms(mockForms);
    setProjects(mockProjects);
    if (mockForms.length > 0) {
      setSelectedForm(mockForms[0]);
    }
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-500 bg-green-50';
      case 'draft': return 'text-gray-500 bg-gray-50';
      case 'completed': return 'text-blue-500 bg-blue-50';
      case 'overdue': return 'text-red-500 bg-red-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getSubmissionStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-500';
      case 'pending': return 'text-yellow-500';
      case 'rejected': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getFileStatusIcon = (file: IntakeFile) => {
    if (file.malwareScanStatus === 'flagged') {
      return <AlertCircle className="w-4 h-4 text-red-500" title="Malware detected" />;
    }
    if (file.schemaValidationStatus === 'invalid') {
      return <XCircle className="w-4 h-4 text-orange-500" title="Schema validation failed" />;
    }
    return <CheckCircle className="w-4 h-4 text-green-500" title="File validated" />;
  };

  const handleCreateForm = () => {
    setIsCreatingForm(true);
    setViewMode('builder');
  };

  const handleEditForm = (form: IntakeForm) => {
    setSelectedForm(form);
    setIsEditingForm(true);
    setViewMode('builder');
  };

  const handleFormSubmit = (submission: IntakeSubmission) => {
    if (onFormSubmit) {
      onFormSubmit(submission);
    } else {
      console.log('Form submitted:', submission);
      // TODO: Implement submission logic
    }
  };

  const renderFormOverview = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900">Intake Forms</h3>
        <button
          onClick={handleCreateForm}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          Create Form
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {forms.map((form) => (
          <motion.div
            key={form.id}
            whileHover={{ scale: 1.02 }}
            className="tyler-card p-6 cursor-pointer"
            onClick={() => setSelectedForm(form)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">{form.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{form.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(form.status)}`}>
                {form.status}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>{form.fields.length} fields</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Due: {form.dueDate}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>Assignee: {form.assignee}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Submissions</span>
                <span className="font-medium text-gray-900">{form.submissions.length}</span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] h-2 rounded-full transition-all"
                  style={{
                    width: `${(form.submissions.filter(s => s.status === 'approved').length / Math.max(form.submissions.length, 1)) * 100}%`
                  }}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditForm(form);
                }}
                className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <Edit3 className="w-4 h-4 inline mr-1" />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode('submissions');
                }}
                className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              >
                <Eye className="w-4 h-4 inline mr-1" />
                View
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderFormBuilder = () => {
    if (!selectedForm && !isCreatingForm) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setViewMode('overview');
              setIsCreatingForm(false);
              setIsEditingForm(false);
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <XCircle className="w-4 h-4" />
            Back to Overview
          </button>
          <h3 className="text-lg font-semibold text-gray-900">
            {isCreatingForm ? 'Create New Form' : `Edit: ${selectedForm?.name}`}
          </h3>
        </div>

        <div className="tyler-card p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
                placeholder="Enter form name"
                defaultValue={selectedForm?.name}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]">
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
                defaultValue={selectedForm?.dueDate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
                placeholder="Enter assignee name"
                defaultValue={selectedForm?.assignee}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
              rows={3}
              placeholder="Describe the purpose of this form..."
              defaultValue={selectedForm?.description}
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">Form Fields</h4>
              <button className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">
                <Plus className="w-4 h-4" />
                Add Field
              </button>
            </div>

            <div className="space-y-4">
              {(selectedForm?.fields || []).map((field, index) => (
                <div key={field.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
                        defaultValue={field.name}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]">
                        <option value="text" selected={field.type === 'text'}>Text</option>
                        <option value="textarea" selected={field.type === 'textarea'}>Text Area</option>
                        <option value="file" selected={field.type === 'file'}>File Upload</option>
                        <option value="select" selected={field.type === 'select'}>Select</option>
                        <option value="date" selected={field.type === 'date'}>Date</option>
                        <option value="number" selected={field.type === 'number'}>Number</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" defaultChecked={field.required} />
                        <span className="text-sm text-gray-700">Required</span>
                      </label>
                      <button className="p-2 text-red-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {field.type === 'select' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Options (comma-separated)</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
                        placeholder="Option 1, Option 2, Option 3"
                        defaultValue={field.options?.join(', ')}
                      />
                    </div>
                  )}

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Help Text</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
                      placeholder="Help text for this field..."
                      defaultValue={field.helpText}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setViewMode('overview');
                setIsCreatingForm(false);
                setIsEditingForm(false);
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button className="px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-md hover:shadow-lg transition-all">
              {isCreatingForm ? 'Create Form' : 'Update Form'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSubmissions = () => {
    if (!selectedForm) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <XCircle className="w-4 h-4" />
            Back to Overview
          </button>
          <h3 className="text-lg font-semibold text-gray-900">
            Submissions: {selectedForm.name}
          </h3>
        </div>

        <div className="space-y-4">
          {selectedForm.submissions.map((submission) => (
            <motion.div
              key={submission.id}
              whileHover={{ scale: 1.01 }}
              className="tyler-card p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium text-gray-900">
                      Submission by {submission.submittedBy}
                    </h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSubmissionStatusColor(submission.status)}`}>
                      {submission.status}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    Submitted on {new Date(submission.submittedAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {Object.entries(submission.data).map(([key, value]) => (
                  <div key={key}>
                    <span className="text-sm font-medium text-gray-700">{key}:</span>
                    <p className="text-sm text-gray-600">{value}</p>
                  </div>
                ))}
              </div>

              {submission.files.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Uploaded Files</h5>
                  <div className="space-y-2">
                    {submission.files.map((file) => (
                      <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FileText className="w-5 h-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getFileStatusIcon(file)}
                          <button className="p-1 text-blue-500 hover:text-blue-600 transition-colors">
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {submission.status === 'pending' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-3">
                    <button className="px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Approve
                    </button>
                    <button className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors">
                      <XCircle className="w-4 h-4 inline mr-1" />
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Client Intake Forms
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Collect client requirements, documents, and information with automated reminders
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={viewMode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {viewMode === 'overview' && renderFormOverview()}
            {viewMode === 'builder' && renderFormBuilder()}
            {viewMode === 'submissions' && renderSubmissions()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
